import api from "./api";
import { jwtDecode } from "jwt-decode";

/**
 * Tipos de respuesta de los diferentes endpoints de autenticación
 */
interface LoginResponse {
  token: string;
  refreshToken: string;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

interface DecodedToken {
  sub: string;
  jti: string;
  role: string;
  userId: string;
  exp: number;
  iss: string;
  aud: string;
}

/**
 * Estructura normalizada de tokens para uso interno
 */
interface NormalizedTokens {
  accessToken: string;
  refreshToken: string;
  decodedToken: DecodedToken;
}

/**
 * Realiza el login con email y password
 * Normaliza la respuesta para uso interno
 */
export async function loginWithCredentials(
  email: string,
  password: string
): Promise<NormalizedTokens> {
  const response = await api.post<LoginResponse>("/Auth/login", {
    email,
    password,
  });

  const { token, refreshToken } = response.data;

  if (!token || !refreshToken) {
    throw new Error("Respuesta inválida del servidor: faltan tokens");
  }

  const accessToken = token;
  const decodedToken = jwtDecode<DecodedToken>(accessToken);

  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);

  return {
    accessToken,
    refreshToken,
    decodedToken,
  };
}

/**
 * Refresca los tokens usando un refresh token
 */
export async function refreshTokens(
  refreshToken: string
): Promise<NormalizedTokens> {
  const response = await api.post<RefreshResponse>("/Auth/refresh", {
    token: refreshToken,
  });

  const { accessToken, refreshToken: newRefreshToken } = response.data;

  if (!accessToken || !newRefreshToken) {
    throw new Error("Respuesta inválida del servidor: faltan tokens");
  }

  const decodedToken = jwtDecode<DecodedToken>(accessToken);

  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", newRefreshToken);

  return {
    accessToken,
    refreshToken: newRefreshToken,
    decodedToken,
  };
}

/**
 * Verifica si el token actual está expirado o próximo a expirar
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const now = Math.floor(Date.now() / 1000);
    return decoded.exp - now < bufferSeconds;
  } catch {
    return true; // Si no se puede decodificar, considerarlo expirado
  }
}

/**
 * Obtiene el token decodificado desde localStorage
 */
export function getDecodedToken(): DecodedToken | null {
  try {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;
    return jwtDecode<DecodedToken>(accessToken);
  } catch {
    return null;
  }
}

/**
 * Limpia todos los tokens del localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("urlToken");
}

/**
 * Verifica si hay tokens válidos en localStorage
 */
export function hasValidTokens(): boolean {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!accessToken || !refreshToken) {
    return false;
  }

  // Verificar si el access token es válido (no expirado)
  try {
    return !isTokenExpired(accessToken);
  } catch {
    return false;
  }
}

/**
 * Intenta renovar los tokens automáticamente si es necesario
 */
export async function ensureValidToken(): Promise<boolean> {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      return false;
    }

    if (isTokenExpired(accessToken)) {
      await refreshTokens(refreshToken);
      return true;
    }

    return true;
  } catch {
    return false;
  }
}
